import React from "react";
import { Box, HStack, Icon, Text } from "@chakra-ui/react";
import { IconType } from "react-icons";
import { alpha, useOppTheme } from "./useOppTheme";
import { Tone, Vocab, words } from "./words";

/**
 * A status as a reader can take it in: a dot, an icon where one is given, and PLAIN WORDS — never a raw
 * enum (POLISH v1 rule 2) — in the tone the vocabulary itself carries, so colour and language can never
 * drift apart (rule 3: meaning twice, in the same place).
 *
 * Pass `vocab` + `value` and `words()` resolves both halves; pass `label`/`tone` directly for the rare
 * status that is not an enum (an overdue count, "No tasks yet").
 */
export function OppStatusPill({ vocab, value, label: labelOverride, tone: toneOverride, icon, size = "md" }: {
  vocab?: Vocab;
  value?: string | null;
  label?: string;
  tone?: Tone;
  icon?: IconType;
  /** `sm` keeps the 14px floor and only tightens the padding — it never shrinks the text. */
  size?: "sm" | "md";
}) {
  const t = useOppTheme();
  const resolved = vocab ? words(vocab, value) : undefined;
  const text = labelOverride ?? resolved?.label ?? value ?? "Not set";
  const tone = toneOverride ?? resolved?.tone ?? "neutral";
  const c = { accent: t.primary, success: t.success, warning: t.warning, danger: t.danger, neutral: t.neutral, purple: t.purple }[tone];
  return (
    <HStack
      as="span"
      spacing={1.5}
      px={size === "sm" ? 2.5 : 3}
      py={1}
      borderRadius="full"
      bg={alpha(c, 0.12)}
      borderWidth="1px"
      borderColor={alpha(c, 0.28)}
      display="inline-flex"
      flexShrink={0}
      maxW="100%"
    >
      {icon
        ? <Icon as={icon} color={c} boxSize={3.5} flexShrink={0} />
        : <Box as="span" w="8px" h="8px" borderRadius="full" bg={c} flexShrink={0} boxShadow={tone === "success" ? `0 0 0 3px ${alpha(c, 0.2)}` : undefined} />}
      {/* fontSize is always `sm` (14px): the readability floor applies inside badges too. */}
      <Text as="span" fontSize="sm" fontWeight="semibold" color={c} noOfLines={1}>{text}</Text>
    </HStack>
  );
}
